import React, {useCallback, useMemo, useState} from 'react';
import {createPortal} from 'react-dom';
import {useTranslation} from 'react-i18next';
import {languagesMap} from '@i18n';
import globalStore from '@services/global.state';
import CorrectBold from '@/components-v2/svgs/correct-bold';

interface LanguageItem {
  name: string;
  locale: string;
}

const CloseIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    className="h-6 w-6 text-t4"
    xmlns="http://www.w3.org/2000/svg">
    <path
      d="M7 7L17 17M17 7L7 17"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

export function useLanguageModal({
  afterHidden,
}: {afterHidden?: () => void} = {}) {
  const {i18n} = useTranslation();
  const [visible, setVisible] = useState(false);
  const [selectedLocale, setSelectedLocale] = useState(
    globalStore.lang || i18n.language,
  );

  const languageItems: LanguageItem[] = useMemo(
    () =>
      Object.keys(languagesMap).map(key => ({
        name: languagesMap[key],
        locale: key,
      })),
    [],
  );

  const hide = useCallback(() => {
    setVisible(false);
    // Keep the legacy first-launch behavior: closing without choosing still
    // persists the current language through globalStore's setter.
    globalStore.lang = globalStore.lang;
    afterHidden?.();
  }, [afterHidden]);

  const show = useCallback(() => {
    setSelectedLocale(globalStore.lang || i18n.language);
    setVisible(true);
  }, [i18n.language]);

  const handleToggle = (locale: string) => {
    setSelectedLocale(locale);
    globalStore.lang = locale;
    i18n.changeLanguage(locale);
    hide();
  };

  const modalNode = visible ? (
    <div className="fixed inset-0 z-[2147483646] flex h-screen w-screen items-end justify-center overflow-hidden bg-black/30 backdrop-blur-md backdrop-saturate-150">
      <button
        type="button"
        aria-label="Close language picker"
        className="absolute inset-0 border-0 bg-transparent p-0"
        onClick={hide}
      />
      <div className="relative z-[2147483647] m-0 flex w-full max-w-[var(--max-screen-width)] flex-col rounded-none rounded-t-lg bg-white p-0 outline-none sm:mx-0 sm:my-0">
        <div className="flex flex-initial justify-between p-5 text-sm font-medium text-t1">
          <span>Please Choose</span>
          <div className="flex-1" />
          <button
            type="button"
            className="flex h-6 w-6 items-center justify-center border-0 bg-transparent p-0"
            onClick={hide}>
            <CloseIcon />
          </button>
        </div>
        <div className="flex max-h-[50vh] flex-col px-5 pb-3 pt-0">
          <div className="flex flex-1 flex-col overflow-hidden">
            {languageItems.map((lang, index) => {
              const active = selectedLocale === lang.locale;
              return (
                <button
                  type="button"
                  key={`${lang.locale}-${index}`}
                  className={`flex h-12 items-center border-0 px-3 text-left text-sm font-medium text-t2 ${
                    active ? 'bg-[var(--bg-color-selected)]' : 'bg-transparent'
                  }`}
                  onClick={() => handleToggle(lang.locale)}>
                  {lang.name}
                  <div className="flex-1" />
                  {active ? <CorrectBold /> : null}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  ) : null;

  const renderModal =
    typeof document !== 'undefined' && modalNode
      ? createPortal(modalNode, document.body)
      : null;

  return {renderModal, show};
}
